import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp, DollarSign, Users } from 'lucide-react';
import Alert from '../common/Alert';
import Button from '../common/Button.js';
import Loader from '../common/Loader.js';
import ListPageHeader from '../common/ListPageHeader.js';
import ListFilterPanel from '../common/ListFilterPanel.js';
import { DashboardLayout } from '../common/page_component/index.js';
import externalCommissionService from '../../services/ExternalCommissionService.js';

const formatRupee = (value) => Math.round(parseFloat(value || 0)).toLocaleString('en-IN');

const PAYMENT_MODES = [
  { value: 'cash', label: 'Cash' },
  { value: 'upi', label: 'UPI' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'online', label: 'Online' },
];

const getStatusBadge = (status) => {
  const map = {
    PENDING: 'bg-yellow-100 text-yellow-700',
    PARTIAL: 'bg-blue-100 text-blue-700',
    PAID: 'bg-green-100 text-green-700',
  };
  return map[status] || 'bg-gray-100 text-gray-700';
};

const rowNetAmount = (row) => Math.round(parseFloat(row.net_settlement_amount ?? row.remaining_settlement_total ?? 0));

const rowNetDirection = (row) => row.net_settlement_direction || (rowNetAmount(row) > 0 ? 'PAY_TO_OWNER' : 'SETTLED');

const rowNetLabel = (row) => {
  const amount = rowNetAmount(row);
  if (amount <= 0) return 'Settled';
  if (rowNetDirection(row) === 'RECEIVE_FROM_OWNER') {
    return `Get ₹${formatRupee(amount)} from owner`;
  }
  return `Pay ₹${formatRupee(amount)} to owner`;
};

const BulkExternalSettlement = () => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [owners, setOwners] = useState([]);
  const [agentId, setAgentId] = useState('');
  const [vehicleOwnerId, setVehicleOwnerId] = useState('');
  const [records, setRecords] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(true);
  const [loadingOwners, setLoadingOwners] = useState(true);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [paymentMode, setPaymentMode] = useState('cash');

  const formId = 'bulk-external-settlement-form';

  const fetchAgents = useCallback(async (ownerFilter = '') => {
    try {
      setLoadingAgents(true);
      const agentList = await externalCommissionService.loadBulkSettlementAgents(ownerFilter);
      setAgents(agentList);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load booking agents');
    } finally {
      setLoadingAgents(false);
    }
  }, []);

  const fetchOwners = useCallback(async (agentFilter = '') => {
    try {
      setLoadingOwners(true);
      const ownerList = await externalCommissionService.loadBulkSettlementOwners(agentFilter);
      setOwners(ownerList);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load vehicle owners');
    } finally {
      setLoadingOwners(false);
    }
  }, []);

  const fetchRecords = useCallback(async (selectedAgentId, selectedOwnerId, fromDate = '', toDate = '') => {
    if (!selectedAgentId || !selectedOwnerId) {
      setRecords([]);
      setTotals(null);
      return;
    }
    try {
      setLoadingRecords(true);
      const params = {};
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const { data, totals: nextTotals } = await externalCommissionService.loadBulkSettlementRecords(
        selectedAgentId,
        selectedOwnerId,
        params,
      );
      setRecords(data || []);
      setTotals(nextTotals || {});
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to load settlement records');
      setRecords([]);
      setTotals(null);
    } finally {
      setLoadingRecords(false);
    }
  }, []);

  useEffect(() => {
    fetchAgents('');
    fetchOwners('');
  }, [fetchAgents, fetchOwners]);

  useEffect(() => {
    fetchAgents(vehicleOwnerId);
  }, [vehicleOwnerId, fetchAgents]);

  useEffect(() => {
    fetchOwners(agentId);
  }, [agentId, fetchOwners]);

  useEffect(() => {
    fetchRecords(agentId, vehicleOwnerId, dateFrom, dateTo);
  }, [agentId, vehicleOwnerId, dateFrom, dateTo, fetchRecords]);

  const summary = useMemo(() => {
    const payRemaining = records.reduce((sum, row) => {
      if (rowNetAmount(row) <= 0) return sum;
      if (rowNetDirection(row) === 'RECEIVE_FROM_OWNER') return sum;
      return sum + rowNetAmount(row);
    }, 0);
    const collectRemaining = records.reduce((sum, row) => {
      if (rowNetAmount(row) <= 0) return sum;
      if (rowNetDirection(row) !== 'RECEIVE_FROM_OWNER') return sum;
      return sum + rowNetAmount(row);
    }, 0);
    const netPay = Math.max(payRemaining - collectRemaining, 0);
    const netCollect = Math.max(collectRemaining - payRemaining, 0);
    return {
      payRemaining,
      collectRemaining,
      netPay,
      netCollect,
      netAmount: netPay > 0 ? netPay : netCollect,
      netDirection: netPay > 0 ? 'PAY_TO_OWNER' : (netCollect > 0 ? 'RECEIVE_FROM_OWNER' : 'SETTLED'),
    };
  }, [records]);

  const dueRecords = useMemo(() => records.filter((row) => rowNetAmount(row) > 0), [records]);

  const selectionReady = agentId && vehicleOwnerId;
  const loadingOptions = loadingAgents || loadingOwners;
  const submitDisabled = submitting || !selectionReady || summary.netAmount <= 0;
  const submitLabel = 'Complete All Settlement';

  const hasActiveFilters = Boolean(agentId || vehicleOwnerId || dateFrom || dateTo);

  const clearFilters = () => {
    setAgentId('');
    setVehicleOwnerId('');
    setDateFrom('');
    setDateTo('');
  };

  const handleCompleteSettlement = async (e) => {
    e.preventDefault();
    if (!agentId) {
      Alert.error('Select a booking agent');
      return;
    }
    if (!vehicleOwnerId) {
      Alert.error('Select a vehicle owner');
      return;
    }
    if (summary.netAmount <= 0) {
      Alert.error('Nothing left to settle for this agent and vehicle owner');
      return;
    }

    const ownerName = owners.find((o) => o.vehicle_owner_id === vehicleOwnerId)?.vehicle_owner_name || vehicleOwnerId;
    const agentName = agents.find((a) => a.agent_id === agentId)?.agent_name || agentId;
    const actionLine = summary.netDirection === 'RECEIVE_FROM_OWNER'
      ? `Get ₹${formatRupee(summary.netCollect)} commission from ${ownerName}`
      : `Pay ₹${formatRupee(summary.netPay)} to ${ownerName}`;

    const confirmed = await Alert.confirm(
      `Complete settlement for ${dueRecords.length} commission(s)?\n\n`
      + `Agent: ${agentName}\nVehicle owner: ${ownerName}\n\n${actionLine}\n\n`
      + 'One payment per commission will be created. Shipments will be marked paid when settlement is complete.',
      'Complete Bulk Settlement',
    );
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const res = await externalCommissionService.bulkSettlementPayment({
        agent_id: agentId,
        vehicle_owner_id: vehicleOwnerId,
        complete_settlement: true,
        payment_mode: paymentMode,
        ...(dateFrom ? { from_date: dateFrom } : {}),
        ...(dateTo ? { to_date: dateTo } : {}),
      });
      Alert.success(res.data?.message || 'Bulk settlement completed');
      fetchRecords(agentId, vehicleOwnerId, dateFrom, dateTo);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Bulk settlement failed');
    } finally {
      setSubmitting(false);
    }
  };

  const netSettlementLabel = summary.netDirection === 'RECEIVE_FROM_OWNER'
    ? `Get ₹${formatRupee(summary.netCollect)} from owner`
    : summary.netPay > 0
      ? `Pay ₹${formatRupee(summary.netPay)} to owner`
      : 'All settled';

  const renderSummary = () => {
    if (!totals || !selectionReady) return null;
    return (
      <>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-3 sm:mt-4 p-3 bg-gray-50 rounded-lg text-xs sm:text-sm">
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500">Total Freight</p>
            <p className="font-semibold truncate">₹{formatRupee(totals.total_freight)}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500">Your Commission</p>
            <p className="font-semibold text-purple-700 truncate">₹{formatRupee(totals.total_agent_commission)}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500">Owner Payment Due</p>
            <p className="font-semibold text-blue-700 truncate">₹{formatRupee(summary.payRemaining)}</p>
          </div>
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-gray-500">Commission to Receive</p>
            <p className="font-semibold text-purple-700 truncate">₹{formatRupee(summary.collectRemaining)}</p>
          </div>
        </div>

        <div className="mt-3 p-3 sm:p-4 border-2 border-orange-200 bg-orange-50 rounded-xl">
          <p className="text-[10px] sm:text-xs font-semibold text-orange-800 uppercase">Net settlement</p>
          <p className="text-lg sm:text-3xl font-bold text-orange-700 mt-1 break-words">{netSettlementLabel}</p>
          <p className="text-[10px] sm:text-xs text-gray-600 mt-1">
            Pay due ₹{formatRupee(summary.payRemaining)} − Commission to receive ₹{formatRupee(summary.collectRemaining)}
          </p>
        </div>
      </>
    );
  };

  const renderMobileCards = () => (
    <div className="xl:hidden space-y-3">
      {records.map((row) => {
        const net = rowNetAmount(row);
        return (
          <div
            key={row.external_commission_id}
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 sm:p-4 border-l-4 border-l-purple-500"
          >
            <div className="flex justify-between items-start gap-2 mb-2">
              <div className="min-w-0 flex-1">
                <button
                  type="button"
                  className="font-bold text-purple-600 text-sm hover:underline"
                  onClick={() => navigate(`/external-commissions/view/${row.external_commission_id}`)}
                >
                  #{row.external_commission_id}
                </button>
                <p className="text-xs text-gray-500 mt-0.5">ML Sheet: {row.ml_sheet_id}</p>
                <p className="text-xs text-gray-500 truncate">{row.vehicle_owner_name || '—'}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold shrink-0 ${getStatusBadge(row.payment_status)}`}>
                {net <= 0 ? 'PAID' : (row.payment_status || 'PENDING')}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 text-xs">
              <div><span className="text-gray-500">Freight:</span> <span className="font-medium">₹{formatRupee(row.total_freight)}</span></div>
              <div><span className="text-gray-500">Commission:</span> <span className="font-medium text-purple-700">₹{formatRupee(row.total_agent_commission)}</span></div>
              <div><span className="text-gray-500">Pay Owner:</span> <span className="font-medium text-blue-700">{Math.round(parseFloat(row.action_pay_to_owner || 0)) > 0 ? `₹${formatRupee(row.action_pay_to_owner)}` : '—'}</span></div>
              <div><span className="text-gray-500">Get Comm.:</span> <span className="font-medium text-purple-700">{Math.round(parseFloat(row.action_get_from_owner || 0)) > 0 ? `₹${formatRupee(row.action_get_from_owner)}` : '—'}</span></div>
            </div>
            <p className="text-xs font-medium text-orange-700 mt-2 pt-2 border-t border-gray-100">{rowNetLabel(row)}</p>
          </div>
        );
      })}
      {dueRecords.length === 0 ? (
        <p className="p-3 text-sm text-green-700 bg-green-50 rounded-xl border border-green-100 text-center">
          All commissions for this agent and vehicle owner are fully settled.
        </p>
      ) : (
        <p className="p-3 text-xs text-gray-500 bg-white rounded-xl border border-gray-100 text-center">
          {dueRecords.length} commission(s) pending settlement.
        </p>
      )}
    </div>
  );

  const renderDesktopTable = () => (
    <div className="hidden xl:block bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-3 text-left">Commission</th>
              <th className="px-3 py-3 text-left">ML Sheet</th>
              <th className="px-3 py-3 text-left">Vehicle Owner</th>
              <th className="px-3 py-3 text-right">Freight</th>
              <th className="px-3 py-3 text-right">Commission</th>
              <th className="px-3 py-3 text-right">Pay Owner</th>
              <th className="px-3 py-3 text-right">Get Comm.</th>
              <th className="px-3 py-3 text-left">Net Action</th>
              <th className="px-3 py-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {records.map((row) => {
              const net = rowNetAmount(row);
              return (
                <tr key={row.external_commission_id} className="hover:bg-gray-50">
                  <td className="px-3 py-3">
                    <button
                      type="button"
                      className="text-secondary font-medium hover:underline"
                      onClick={() => navigate(`/external-commissions/view/${row.external_commission_id}`)}
                    >
                      {row.external_commission_id}
                    </button>
                  </td>
                  <td className="px-3 py-3">{row.ml_sheet_id}</td>
                  <td className="px-3 py-3 text-xs">{row.vehicle_owner_name || '—'}</td>
                  <td className="px-3 py-3 text-right">₹{formatRupee(row.total_freight)}</td>
                  <td className="px-3 py-3 text-right text-purple-700">₹{formatRupee(row.total_agent_commission)}</td>
                  <td className="px-3 py-3 text-right text-blue-700">
                    {Math.round(parseFloat(row.action_pay_to_owner || 0)) > 0
                      ? `₹${formatRupee(row.action_pay_to_owner)}`
                      : '—'}
                  </td>
                  <td className="px-3 py-3 text-right text-purple-700">
                    {Math.round(parseFloat(row.action_get_from_owner || 0)) > 0
                      ? `₹${formatRupee(row.action_get_from_owner)}`
                      : '—'}
                  </td>
                  <td className="px-3 py-3 text-xs font-medium text-orange-700">{rowNetLabel(row)}</td>
                  <td className="px-3 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${getStatusBadge(row.payment_status)}`}>
                      {net <= 0 ? 'PAID' : (row.payment_status || 'PENDING')}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {dueRecords.length === 0 ? (
        <p className="p-4 text-sm text-green-700 bg-green-50 border-t border-green-100">
          All commissions for this agent and vehicle owner are fully settled.
        </p>
      ) : (
        <p className="p-4 text-xs text-gray-500 border-t border-gray-100">
          {dueRecords.length} commission(s) pending. Use <strong>Complete All Settlement</strong> to record payments and update shipments.
        </p>
      )}
    </div>
  );

  return (
    <DashboardLayout>
      <div className="max-w-full mx-auto min-w-0">
        <ListPageHeader
          title="Bulk Agent Settlement"
          icon={Users}
          titleClassName="text-secondary"
          iconClassName="w-5 h-5 text-secondary shrink-0"
          backTo="/external-commissions"
          className="mb-3 sm:mb-4"
        >
          <Button
            mobileIconOnly
            type="submit"
            form={formId}
            variant="primary"
            icon={DollarSign}
            disabled={submitDisabled}
            loading={submitting}
            mobileLabel={submitLabel}
          >
            {submitLabel}
          </Button>
        </ListPageHeader>

        {/* Collapsible info — compact on mobile */}
        <div className="mb-3 sm:mb-4 bg-blue-50 border border-blue-100 rounded-xl overflow-hidden">
          <button
            type="button"
            onClick={() => setShowInfo((prev) => !prev)}
            className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left md:hidden"
          >
            <span className="text-xs font-semibold text-gray-800">How net settlement works</span>
            {showInfo ? <ChevronUp className="w-4 h-4 shrink-0 text-gray-500" /> : <ChevronDown className="w-4 h-4 shrink-0 text-gray-500" />}
          </button>
          <div className={`text-xs sm:text-sm text-gray-700 px-3 pb-3 md:p-4 ${showInfo ? 'block' : 'hidden md:block'}`}>
            <p className="font-semibold text-gray-800 mb-1 hidden md:block">Net settlement (simple)</p>
            <p>
              Select a <strong>booking agent</strong> and <strong>vehicle owner</strong> to load matching commission records.
              Net = Pay to vehicle owner − Your commission to receive from owner.
            </p>
            <ul className="mt-2 space-y-1 list-disc list-inside text-[11px] sm:text-xs">
              <li>Positive net → you <strong>pay</strong> the vehicle owner.</li>
              <li>Negative net → you <strong>collect</strong> commission from the owner.</li>
              <li>When done, commission is marked settled and shipments update to paid.</li>
            </ul>
          </div>
        </div>

        {loadingOptions ? (
          <Loader fullScreen={false} message="Loading settlement options..." />
        ) : (
          <form id={formId} onSubmit={handleCompleteSettlement} className="space-y-3 sm:space-y-4">
            <ListFilterPanel
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              title="Settlement Filters"
            >
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Booking Agent *</label>
                <SelectField
                  variant="filter"
                  value={agentId}
                  onChange={(e) => setAgentId(e.target.value)}
                  className="w-full"
                  required
                >
                  <option value="">Select agent...</option>
                  {agents.map((a) => (
                    <option key={a.agent_id} value={a.agent_id}>
                      {a.agent_name} ({a.agent_id})
                    </option>
                  ))}
                </SelectField>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Vehicle Owner *</label>
                <SelectField
                  variant="filter"
                  value={vehicleOwnerId}
                  onChange={(e) => setVehicleOwnerId(e.target.value)}
                  className="w-full"
                  required
                >
                  <option value="">Select vehicle owner...</option>
                  {owners.map((o) => (
                    <option key={o.vehicle_owner_id} value={o.vehicle_owner_id}>
                      {o.vehicle_owner_name} ({o.vehicle_owner_id})
                    </option>
                  ))}
                </SelectField>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">From Date</label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">To Date</label>
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom || undefined}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-full px-3 sm:px-4 py-1.5 sm:py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">Payment Mode</label>
                <SelectField
                  variant="filter"
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="w-full"
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </SelectField>
              </div>
            </ListFilterPanel>

            {renderSummary()}

            <div className="md:hidden">
              <Button
                type="submit"
                variant="primary"
                icon={DollarSign}
                disabled={submitDisabled}
                loading={submitting}
                fullWidth
              >
                {submitLabel}
              </Button>
            </div>

            {selectionReady && loadingRecords && (
              <Loader fullScreen={false} message="Loading records..." />
            )}

            {selectionReady && !loadingRecords && records.length === 0 && (
              <p className="text-sm text-gray-500 bg-white rounded-xl p-4 sm:p-6 shadow-sm text-center">
                No commission records for this booking agent and vehicle owner combination.
              </p>
            )}

            {selectionReady && !loadingRecords && records.length > 0 && (
              <>
                {renderDesktopTable()}
                {renderMobileCards()}
              </>
            )}
          </form>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BulkExternalSettlement;
